import { useEffect, useState } from "react";
import {
  FileText,
  FloppyDisk,
  ImageSquare,
  PencilSimple,
  Plus,
  Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, API_ROOT } from "@/lib/api";
import { useGet } from "@/hooks/useResource";
import { ConfirmationStamp, StatusBadge } from "@/components/common";
import { MenuSelection } from "@/components/menu/MenuSelection";
import { DrinksSelection } from "@/components/menu/DrinksSelection";
import {
  COURSES,
  drinksFormFromDoc,
  drinksPayload,
  itemNames,
  menuFormFromDoc,
  menuPayload,
} from "@/lib/menuModule";
import { GuestFormDialog, BLANK_GUEST } from "@/components/guests/GuestFormDialog";
import { GuestNumbersPanel } from "@/components/guests/GuestNumbersPanel";
import { GuestRow } from "@/components/guests/GuestRow";
import { guestType } from "@/lib/guests";
import { ARBOUR_NAMES, CEREMONY_LOCATION_NAMES } from "@/lib/ceremonyOptions";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const MODULES = [
  {
    key: "ceremony",
    title: "Ceremony",
    confirmable: true,
    summary: (data) => data.location || "No selection saved",
    fields: [
      ["location", "Ceremony location", "select", CEREMONY_LOCATION_NAMES],
      ["start_time", "Start time", "time"],
      ["arbour_choice", "Arbour choice", "select", ARBOUR_NAMES],
      ["additional_chairs", "Additional chairs", "number"],
      ["guest_seating", "Guest seating"],
      ["celebrant", "Celebrant"],
      ["celebrant_contact", "Celebrant contact"],
      ["stylist", "Stylist"],
      ["stylist_contact", "Stylist contact"],
      ["processional_song", "Processional song"],
      ["recessional_song", "Recessional song"],
      ["readings", "Readings", "textarea"],
      ["notes", "Notes", "textarea"],
    ],
  },
  {
    key: "menu",
    title: "Menu",
    confirmable: true,
    kind: "menu",
    summary: (data, catalogue) => {
      const counts = COURSES.map((course) => {
        const names = itemNames(catalogue, data[course.key]);
        return names.length ? `${course.label}: ${names.join(", ")}` : null;
      }).filter(Boolean);
      if (!data.package && counts.length === 0) {
        return "No selection saved";
      }
      return [data.package, ...counts].filter(Boolean).join(" · ");
    },
  },
  {
    key: "drinks",
    title: "Drinks",
    confirmable: true,
    kind: "drinks",
    summary: (data) => data.package || "No selection saved",
  },
];

const EMPTY_SUPPLIER = { name: "", category: "", contact: "", phone: "", notes: "" };

export function PlanningInputsEditor({ eventId, planning, runSheetApproval, onSaved }) {
  const [module, setModule] = useState(null);
  const [guest, setGuest] = useState(null);
  const [guestForm, setGuestForm] = useState(BLANK_GUEST);
  const [guestSaving, setGuestSaving] = useState(false);
  const [supplier, setSupplier] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [consentSaving, setConsentSaving] = useState("");
  const { data: catalogue } = useGet("/menu-items");

  const guests = planning.guest_entries || [];
  const suppliers = planning.supplier_contacts || [];
  const moodBoard = planning.mood_board || [];
  const runSheet = planning.run_sheet;
  const runApproved = runSheetApproval?.state === "approved";

  const unlockModule = async (key, title) => {
    try {
      await api.put(`/staff/events/${eventId}/planning/${key}`, { client_status: "draft" });
      toast.success(`${title} unlocked for client edits.`);
      onSaved?.();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const openAddGuest = () => {
    setGuestForm(BLANK_GUEST);
    setGuest({ mode: "create" });
  };
  const openEditGuest = (entry) => {
    setGuestForm({ ...BLANK_GUEST, ...entry, type: guestType(entry) });
    setGuest({ mode: "edit", id: entry.id });
  };

  const saveGuest = async () => {
    if (!guestForm.name.trim()) {
      toast.error("Please enter a guest name.");
      return;
    }
    setGuestSaving(true);
    const payload = { ...guestForm, name: guestForm.name.trim() };
    try {
      if (guest?.id) {
        await api.put(`/staff/events/${eventId}/guests/${guest.id}`, payload);
        toast.success("Guest updated.");
      } else {
        await api.post(`/staff/events/${eventId}/guests`, payload);
        toast.success("Guest added.");
      }
      setGuest(null);
      onSaved?.();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setGuestSaving(false);
    }
  };

  const saveConsent = async (decision) => {
    setConsentSaving(decision);
    try {
      await api.put(`/staff/events/${eventId}/planning/consent`, { decision });
      toast.success("Consent decision recorded.");
      onSaved?.();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setConsentSaving("");
    }
  };

  const confirmRemoval = async () => {
    const target = removing;
    setRemoving(null);
    if (!target) return;
    try {
      await api.delete(`/staff/events/${eventId}/${target.path}/${target.id}`);
      toast.success(`${target.label} removed.`);
      onSaved?.();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  return (
    <section
      className="border border-border bg-card p-6 rounded-lg"
      data-testid="staff-planning-inputs-editor"
    >
      <div>
        <p className="eyebrow">Client planning inputs</p>
        <p className="text-sm text-muted-foreground mt-1">
          Every client input is editable here. Confirmed inputs show who confirmed them and when.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
        {MODULES.map((definition) => {
          const data = planning[definition.key] || {};
          return (
            <EditableSummary
              key={definition.key}
              title={definition.title}
              value={definition.summary(data, catalogue)}
              testId={`staff-edit-${definition.key}`}
              onEdit={() => setModule(definition)}
              badge={
                <StatusBadge
                  status={data.client_status}
                  at={data.confirmed_at}
                  by={data.confirmed_by}
                  testid={`staff-${definition.key}-status-badge`}
                />
              }
              stamp={
                <ConfirmationStamp
                  label="Client confirmed"
                  at={data.confirmed_at}
                  by={data.confirmed_by}
                  testid={`staff-${definition.key}-confirmed-stamp`}
                />
              }
              action={
                data.client_status === "confirmed" ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 rounded-full"
                    onClick={() => unlockModule(definition.key, definition.title)}
                    data-testid={`staff-unlock-${definition.key}`}
                  >
                    Unlock for client edits
                  </Button>
                ) : null
              }
            />
          );
        })}

        <EditableSummary
          title="Run sheet"
          testId="staff-run-sheet-card"
          value={
            runSheet
              ? `${runSheet.original_filename} · v${runSheet.version || 1}`
              : "Not issued yet"
          }
          hint="Upload or replace the run sheet in the Documents tab."
          badge={
            <StatusBadge
              status={runApproved ? "confirmed" : "pending"}
              at={runSheetApproval?.updated_at}
              by={runSheetApproval?.approved_by}
              confirmedLabel="Approved"
              pendingLabel={runSheet ? "Awaiting approval" : "Not issued"}
              testid="staff-run_sheet-status-badge"
            />
          }
          stamp={
            runApproved ? (
              <ConfirmationStamp
                label="Client approved"
                at={runSheetApproval?.updated_at}
                by={runSheetApproval?.approved_by}
                testid="staff-run-sheet-approved-stamp"
              />
            ) : null
          }
        />

        <article className="border border-border p-4 rounded-lg" data-testid="staff-event-documents-summary">
          <p className="eyebrow">Event documents</p>
          {planning.event_documents?.length ? (
            <ul className="mt-2 space-y-1 text-sm text-primary">
              {planning.event_documents.slice(0, 3).map((file) => (
                <li key={file.id} className="truncate">{file.original_filename}</li>
              ))}
              {planning.event_documents.length > 3 && (
                <li className="text-muted-foreground">
                  +{planning.event_documents.length - 3} more
                </li>
              )}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">— No uploads yet</p>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            Shared documents live in the Documents tab.
          </p>
        </article>
      </div>

      {/* ---- Guests: list + numbers, mirroring the client page ---- */}
      <section className="mt-8 border-t border-border pt-6" data-testid="staff-guest-entries">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-2xl text-primary">Guests ({guests.length})</h3>
            <p className="text-sm text-muted-foreground mt-1">
              The couple's guest list and their final numbers.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge
              status={planning.guest_numbers?.client_status}
              at={planning.guest_numbers?.confirmed_at}
              by={planning.guest_numbers?.confirmed_by}
              testid="staff-guest_numbers-status-badge"
            />
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={openAddGuest}
              data-testid="staff-add-guest-btn"
            >
              <Plus size={15} className="mr-1" /> Add guest
            </Button>
          </div>
        </div>

        {guests.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No guests added yet.</p>
        ) : (
          <div className="mt-4 max-h-80 overflow-y-auto divide-y divide-border rounded-lg border border-border">
            {guests.map((entry) => (
              <GuestRow
                key={entry.id}
                guest={entry}
                onEdit={openEditGuest}
                onDelete={(target) =>
                  setRemoving({ path: "guests", id: target.id, label: target.name || "Guest" })
                }
              />
            ))}
          </div>
        )}

        <div className="mt-5" data-testid="staff-guest-numbers-panel">
          <GuestNumbersPanel
            stored={planning.guest_numbers}
            guests={guests}
            heading="Final guest count"
            onSave={async (payload, clientStatus) => {
              await api.put(`/staff/events/${eventId}/planning/guest_numbers`, {
                ...payload,
                client_status: clientStatus,
              });
              onSaved?.();
            }}
            onUnlock={
              planning.guest_numbers?.client_status === "confirmed"
                ? () => unlockModule("guest_numbers", "Guest numbers")
                : undefined
            }
          />
          {planning.guest_numbers?.client_status === "confirmed" && (
            <p className="mt-3 text-xs text-muted-foreground">
              Confirmed numbers are locked for the couple. Saving here records the change against
              your name.
            </p>
          )}
        </div>
      </section>

      {/* ---- Client supplier contacts ---- */}
      <section className="mt-8 border-t border-border pt-6" data-testid="staff-supplier-entries">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-2xl text-primary">
              Client supplier contacts ({suppliers.length})
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              The couple's own booked suppliers for this event.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => setSupplier({ ...EMPTY_SUPPLIER })}
            data-testid="staff-add-supplier-contact-btn"
          >
            <Plus size={15} className="mr-1" /> Add contact
          </Button>
        </div>
        {suppliers.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No supplier contacts saved yet.</p>
        ) : (
          <div className="mt-4 max-h-72 overflow-y-auto divide-y divide-border rounded-lg border border-border">
            {suppliers.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between gap-3 p-4">
                <button
                  type="button"
                  onClick={() => setSupplier({ ...EMPTY_SUPPLIER, ...entry })}
                  className="flex min-w-0 flex-1 items-center justify-between gap-4 text-left hover:text-secondary"
                  data-testid={`staff-edit-supplier-contact-${entry.id}`}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-heading text-lg text-primary">
                      {entry.name}
                    </span>
                    <span className="mt-0.5 block truncate text-xs uppercase tracking-wide text-muted-foreground">
                      {[entry.category, entry.contact, entry.phone].filter(Boolean).join(" · ") ||
                        "No details saved"}
                    </span>
                  </span>
                  <PencilSimple size={18} className="shrink-0" />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setRemoving({
                      path: "supplier-contacts",
                      id: entry.id,
                      label: entry.name || "Contact",
                    })
                  }
                  aria-label={`Delete ${entry.name}`}
                  className="shrink-0 rounded-lg p-2 text-muted-foreground transition-colors duration-200 hover:bg-destructive/10 hover:text-destructive"
                  data-testid={`staff-delete-supplier-contact-${entry.id}`}
                >
                  <Trash size={17} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ---- Social media consent ---- */}
      <section className="mt-8 border-t border-border pt-6" data-testid="staff-consent-section">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-heading text-2xl text-primary">Social media consent</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {planning.consent?.decision
                ? `Recorded decision: ${planning.consent.decision}`
                : "No decision recorded yet."}
            </p>
          </div>
          <StatusBadge
            status={planning.consent?.decision ? "confirmed" : "pending"}
            at={planning.consent?.decided_at}
            by={planning.consent?.decided_by}
            testid="staff-consent-status-badge"
          />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            variant={planning.consent?.decision === "approved" ? "default" : "outline"}
            size="sm"
            className="rounded-full"
            disabled={!!consentSaving}
            onClick={() => saveConsent("approved")}
            data-testid="staff-consent-approve-btn"
          >
            {consentSaving === "approved" ? "Saving…" : "Record approved"}
          </Button>
          <Button
            variant={planning.consent?.decision === "declined" ? "default" : "outline"}
            size="sm"
            className="rounded-full"
            disabled={!!consentSaving}
            onClick={() => saveConsent("declined")}
            data-testid="staff-consent-decline-btn"
          >
            {consentSaving === "declined" ? "Saving…" : "Record declined"}
          </Button>
          <ConfirmationStamp
            label="Recorded"
            at={planning.consent?.decided_at}
            by={planning.consent?.decided_by}
            testid="staff-consent-recorded-stamp"
          />
        </div>
      </section>

      {/* ---- Mood board (last) ---- */}
      <section className="mt-8 border-t border-border pt-6" data-testid="staff-mood-board-summary">
        <h3 className="font-heading text-2xl text-primary">Mood board ({moodBoard.length})</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Inspiration the couple uploaded. Click any item to open the full-size file.
        </p>
        {moodBoard.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No uploads yet.</p>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {moodBoard.map((file) => (
              <a
                key={file.id}
                href={`${API_ROOT}/staff/documents/${file.id}/file`}
                target="_blank"
                rel="noreferrer"
                className="group overflow-hidden rounded-lg border border-border bg-muted transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                data-testid={`staff-mood-board-item-${file.id}`}
              >
                {String(file.mime || "").startsWith("image/") ? (
                  <img
                    src={`${API_ROOT}/staff/documents/${file.id}/file`}
                    alt={file.original_filename}
                    className="h-32 w-full object-cover"
                    data-testid={`staff-mood-board-preview-${file.id}`}
                  />
                ) : (
                  <span className="flex h-32 w-full items-center justify-center text-muted-foreground">
                    {String(file.mime || "").includes("pdf") ? (
                      <FileText size={26} weight="light" />
                    ) : (
                      <ImageSquare size={26} weight="light" />
                    )}
                  </span>
                )}
                <span className="block truncate border-t border-border bg-card px-3 py-2 text-xs text-muted-foreground">
                  {file.original_filename}
                </span>
              </a>
            ))}
          </div>
        )}
      </section>

      <ModuleDialog
        definition={module}
        data={planning[module?.key] || {}}
        catalogue={catalogue}
        eventId={eventId}
        onClose={() => setModule(null)}
        onSaved={onSaved}
      />
      <GuestFormDialog
        open={!!guest}
        onOpenChange={(open) => !open && setGuest(null)}
        editing={!!guest?.id}
        form={guestForm}
        setForm={setGuestForm}
        onSave={saveGuest}
        saving={guestSaving}
      />
      <SupplierDialog
        supplier={supplier}
        eventId={eventId}
        onClose={() => setSupplier(null)}
        onSaved={onSaved}
      />
      <AlertDialog open={!!removing} onOpenChange={(open) => !open && setRemoving(null)}>
        <AlertDialogContent data-testid="staff-remove-entry-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl font-light">
              Remove {removing?.label}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the entry from the couple's event record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-destructive hover:bg-destructive/90"
              onClick={confirmRemoval}
              data-testid="staff-confirm-remove-entry"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function EditableSummary({ title, value, onEdit, testId, stamp, badge, action, hint }) {
  return (
    <article className="border border-border p-4 rounded-lg" data-testid={onEdit ? undefined : testId}>
      <div className="flex items-start justify-between gap-2">
        <p className="eyebrow">{title}</p>
        {badge}
      </div>
      <p className="mt-2 text-sm text-primary break-words">{value || "No selection saved"}</p>
      {stamp && <div className="mt-3">{stamp}</div>}
      {hint && <p className="mt-3 text-xs text-muted-foreground">{hint}</p>}
      {onEdit && (
        <Button
          variant="outline"
          size="sm"
          className="mt-4 rounded-full"
          onClick={onEdit}
          data-testid={testId}
        >
          <PencilSimple size={15} className="mr-1" /> Edit
        </Button>
      )}
      {action}
    </article>
  );
}

function ModuleDialog({ definition, data, catalogue, eventId, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [status, setStatus] = useState("draft");
  const [saving, setSaving] = useState(false);
  const kind = definition?.kind;
  useEffect(() => {
    if (definition) {
      if (definition.kind === "menu") {
        setForm(menuFormFromDoc(data));
      } else if (definition.kind === "drinks") {
        setForm(drinksFormFromDoc(data));
      } else {
        setForm(toForm(definition, data));
      }
      setStatus(data.client_status === "confirmed" ? "confirmed" : "draft");
    }
  }, [definition, data]);
  const save = async () => {
    setSaving(true);
    const payload =
      kind === "menu"
        ? menuPayload(form)
        : kind === "drinks"
          ? drinksPayload(form)
          : fromForm(definition, form);
    if (definition.confirmable) {
      payload.client_status = status;
    }
    try {
      await api.put(`/staff/events/${eventId}/planning/${definition.key}`, payload);
      toast.success(`${definition.title} updated.`);
      onSaved();
      onClose();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!definition} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={`max-h-[90vh] overflow-y-auto ${kind ? "max-w-4xl" : "max-w-2xl"}`}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            Edit {definition?.title}
          </DialogTitle>
          <DialogDescription>
            These changes update the saved client planning input.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          {definition?.confirmable && (
            <div className="mb-5 max-w-sm">
              <Label className="eyebrow">Confirmation status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-2" data-testid="staff-planning-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Pending (client can edit)</SelectItem>
                  <SelectItem value="confirmed">Confirmed (locked for client)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {kind === "menu" && (
            <MenuSelection catalogue={catalogue} form={form} setForm={setForm} />
          )}
          {kind === "drinks" && (
            <DrinksSelection catalogue={catalogue} form={form} setForm={setForm} />
          )}
          {!kind && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {definition?.fields.map(([key, label, type, options]) => (
                <div key={key} className={type === "textarea" ? "sm:col-span-2" : ""}>
                  <FormField
                    label={label}
                    type={type}
                    options={options}
                    value={form[key]}
                    onChange={(value) => setForm({ ...form, [key]: value })}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="rounded-full bg-primary hover:bg-primary/90"
            onClick={save}
            disabled={saving}
            data-testid="save-staff-planning-btn"
          >
            <FloppyDisk size={16} className="mr-1" />
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SupplierDialog({ supplier, eventId, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_SUPPLIER);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (supplier) {
      setForm({ ...EMPTY_SUPPLIER, ...supplier });
    }
  }, [supplier]);
  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Supplier name is required.");
      return;
    }
    setSaving(true);
    try {
      if (supplier?.id) {
        await api.put(`/staff/events/${eventId}/supplier-contacts/${supplier.id}`, form);
        toast.success("Supplier contact updated.");
      } else {
        await api.post(`/staff/events/${eventId}/supplier-contacts`, form);
        toast.success("Supplier contact added.");
      }
      onSaved();
      onClose();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!supplier} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            {supplier?.id ? "Edit supplier contact" : "Add supplier contact"}
          </DialogTitle>
          <DialogDescription>
            These are the couple's own booked suppliers for this event.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <FormField label="Name" value={form.name} onChange={(value) => setForm({ ...form, name: value })} />
          <FormField label="Category" value={form.category} onChange={(value) => setForm({ ...form, category: value })} />
          <FormField label="Email / contact" value={form.contact} onChange={(value) => setForm({ ...form, contact: value })} />
          <FormField label="Phone" value={form.phone} onChange={(value) => setForm({ ...form, phone: value })} />
          <FormField label="Notes" type="textarea" value={form.notes} onChange={(value) => setForm({ ...form, notes: value })} />
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="rounded-full bg-primary hover:bg-primary/90"
            onClick={save}
            disabled={saving}
            data-testid="save-staff-supplier-btn"
          >
            {saving ? "Saving…" : supplier?.id ? "Save contact" : "Add contact"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, type = "text", value, onChange, options }) {
  const testId = `staff-planning-${label.toLowerCase().replaceAll(/[^a-z0-9]+/g, "-")}-input`;
  if (type === "select") {
    return (
      <div>
        <Label className="eyebrow">{label}</Label>
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger className="mt-2" data-testid={testId}>
            <SelectValue placeholder={`Choose ${label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {(options || []).map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      {type === "textarea" ? (
        <Textarea
          className="mt-2"
          rows={3}
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
          data-testid={testId}
        />
      ) : (
        <Input
          className="mt-2"
          type={type === "time" || type === "number" ? type : "text"}
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
          data-testid={testId}
        />
      )}
      {type === "list" && (
        <p className="mt-1 text-xs text-muted-foreground">Separate items with a comma.</p>
      )}
    </div>
  );
}

function toForm(definition, data) {
  if (!definition) {
    return {};
  }
  return Object.fromEntries(
    (definition.fields || []).map(([key, , type]) => [
      key,
      type === "list" && Array.isArray(data[key]) ? data[key].join(", ") : data[key] || "",
    ]),
  );
}

function fromForm(definition, form) {
  return Object.fromEntries(
    (definition.fields || []).map(([key, , type]) => [
      key,
      type === "list"
        ? String(form[key] || "")
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
        : form[key] || "",
    ]),
  );
}
